import React, { useMemo } from 'react'
import classes from './card.module.css'
import CardImage from './CardImage/CardImage'
import {
    DayIndicator,
    PremiumIndicator,
    WishList,
} from './CardBadges/CardBadges'
import CardTitle from './CardTitle/CardTitle'
import CardBody from './CardBody/CardBody'
import CardDetails from './CardDetails/CardDetails'
import moment from 'moment'
import classNames from 'classnames'
import { Link, useLocation, useHistory } from 'react-router-dom'
import avatar from '../../../assets/images/avatar-dark.svg'
import { IMG_URL } from '../../../api/api'
import {
    addToWishlist,
    isItemInWishlist,
    removeFromWishlist,
} from '../../../pages/WishList/wishListApi'
import { useDispatch, useSelector } from 'react-redux'
import { wishListSelectors } from '../../../pages/WishList/wishListSlice'
import { useAuthReminderModal } from '../../../hooks/useAuthReminderModal'
import { getImageURL } from '../../../util/functions'
import blogPlaceholder from '../../../assets/images/blog-placeholder.svg'
import coursePlaceholder from '../../../assets/images/course-placeholder.svg'
import classPlaceholder from '../../../assets/images/class-placeholder.svg'
import { userSelectors } from '../../../redux/userSlice'
import notification from '../../Notification/notification'

export const Card = ({ children, className, onClick, url = '', ...props }) => {
    return !!url ? (
        <Link
            onClick={onClick}
            className={classNames(classes.card, className)}
            {...props}
        >
            {children}
        </Link>
    ) : (
        <div className={classNames(classes.card, className)} {...props}>
            {children}
        </div>
    )
}

export const ClassCard = ({
    url,
    title,
    category,
    price,
    attendees,
    date,
    time,
    className,
    classid,
    ...props
}) => {
    const wishListClasses = useSelector(wishListSelectors.classes)
    const itemInWishlist = isItemInWishlist(wishListClasses, classid)
    const isLoggedIn = useSelector(userSelectors.isLoggedIn)

    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()

    const { renderModal, conditionallyShowModal } = useAuthReminderModal(
        location.pathname
    )

    const onToggleWishlist = e => {
        if (itemInWishlist) {
            dispatch(
                removeFromWishlist({
                    itemId: classid,
                    type: 'class',
                })
            )
        } else {
            dispatch(
                addToWishlist({
                    itemId: classid,
                    type: 'class',
                })
            )
        }
    }

    const handleWishlistClick = event => {
        event.preventDefault() // To prevent link triggering
        conditionallyShowModal(onToggleWishlist)(event)
    }

    const handleNavigation = () => {
        // if (isLoggedIn) {
        //     history.push(`/app/browse/classes/${classid}`)
        //     return
        // }
        // notification.info({
        //     title: 'Please sign in',
        // })
        history.push(`/app/browse/classes/${classid}`)
    }

    return (
        <React.Fragment>
            <Card
                className={classNames(
                    className,
                    classes.classCard,
                    classes.cardFullHeight
                )}
                onClick={handleNavigation}
                url={`/app/browse/classes/${classid}`}
                {...props}
            >
                <CardImage
                    url={url}
                    variant={'FULL'}
                    badges={[
                        <WishList
                            filled={itemInWishlist}
                            onClick={handleWishlistClick}
                        />,
                        <DayIndicator
                            day={moment(date)
                                .format('ddd')
                                .toUpperCase()}
                            dayNumber={moment(date).format('DD')}
                        />,
                    ]}
                    errorImageUrl={classPlaceholder}
                />
                <CardBody>
                    <CardTitle hasEnrolled={props.isEnrolled} isClass>
                        {title}
                    </CardTitle>
                    <div className={classes.cardDetails}>
                        <span className={classes.cardDetail}>
                            {moment(date).format('ddd, MMM DD')}, {time}
                        </span>
                        {category && (
                            <span
                                className={classNames(
                                    classes.cardDetail,
                                    classes.cardDetailUpperCase
                                )}
                            >
                                {category}
                            </span>
                        )}
                    </div>
                    <CardDetails attendees={attendees} price={price} />
                </CardBody>
            </Card>
            {renderModal()}
        </React.Fragment>
    )
}

export const CourseCard = ({
    premium = false,
    url,
    title,
    category,
    price,
    courseType,
    attendees,
    className = '',
    coursecode,
    showStudentCount = true,
    ...props
}) => {
    const courses = useSelector(wishListSelectors.courses)
    const itemInWishlist = isItemInWishlist(courses, coursecode)
    const isLoggedIn = useSelector(userSelectors.isLoggedIn)

    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()

    const { renderModal, conditionallyShowModal } = useAuthReminderModal(
        location.pathname
    )

    const onToggleWishlist = e => {
        if (itemInWishlist) {
            dispatch(
                removeFromWishlist({
                    itemId: coursecode,
                    type: 'course',
                })
            )
        } else {
            dispatch(
                addToWishlist({
                    itemId: coursecode,
                    type: 'course',
                })
            )
        }
    }

    const handleWishlistClick = event => {
        event.preventDefault() // To prevent link triggering
        conditionallyShowModal(onToggleWishlist)(event)
    }

    const handleNavigation = () => {
        // if (isLoggedIn) {
        //     history.push(`/app/browse/courses/${coursecode}`)
        //     return
        // }
        // notification.info({
        //     title: 'Please sign in',
        // })
        // history.push(`/app/auth/sign-in`)
        history.push(`/app/browse/courses/${coursecode}`)
    }

    return (
        <React.Fragment>
            <Card
                {...props}
                className={classNames(
                    className,
                    classes.cardFullHeight,
                    premium && classes.premiumCard
                )}
                onClick={handleNavigation}
                url={`/app/browse/courses/${coursecode}`}
            >
                <CardImage
                    url={url}
                    variant={'HALF'}
                    badges={[
                        <WishList
                            half
                            filled={itemInWishlist}
                            onClick={handleWishlistClick}
                        />,
                        premium && <PremiumIndicator />,
                    ]}
                    errorImageUrl={coursePlaceholder}
                />
                <CardBody>
                    <CardTitle
                        courseType={courseType}
                        hasEnrolled={props.isEnrolled}
                    >
                        {title}
                    </CardTitle>
                    <p className={classes.cardCategory}>{category}</p>
                    <CardDetails
                        price={price}
                        attendees={attendees}
                        showStudentCount={showStudentCount}
                        className={classes.courseCardDetails}
                    />
                </CardBody>
            </Card>
            {renderModal()}
        </React.Fragment>
    )
}

export const BlogPostCard = ({
    author,
    authorImage,
    date,
    title,
    imageUrl,
    className = '',
    postId = null,
    ...props
}) => {
    const history = useHistory()

    return (
        <Card
            className={classNames(className, classes.cardCurved)}
            url={`/blog/${postId}`}
            onClick={() => history.push(`/blog/${postId}`)}
            {...props}
        >
            <CardImage
                url={imageUrl ? getImageURL(imageUrl) : blogPlaceholder}
                variant={'CURVED'}
                errorImageUrl={blogPlaceholder}
            />
            <div className={classes.blogCardDetailsWrapper}>
                <div className={classes.blogCardDetails}>
                    <img
                        src={authorImage || avatar}
                        alt="Author Avatar"
                        className={classes.blogCardDetailsImage}
                    />
                    <span className={classes.blogCardDetailsAuthor}>
                        {author}
                    </span>
                    {/* <span className={classes.blogCardDetailsDate}>
                        {moment(date).format('MMM DD, YYYY')}
                    </span> */}
                </div>
            </div>
            <CardBody className={classes.blogCardBody}>
                <CardTitle variant={'BOLD'}>{title}</CardTitle>
            </CardBody>
        </Card>
    )
}
